// HV.1 — Painel de homologação do Business Builder.

import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { getHomologacaoOverview, type HomologacaoOverview } from "@/lib/homologacao.functions";
import { editorialLabel } from "@/lib/business-plan/schema";
import { isHomologModeOn, setHomologMode } from "@/components/business-builder/HomologacaoObserver";
import { Eye, EyeOff } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/homologacao")({
  head: () => ({ meta: [
    { title: "Homologação — Business Builder" },
    { name: "robots", content: "noindex, nofollow" },
  ] }),
  component: HomologacaoPage,
});

function fmtDuration(ms: number | null): string {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), r = s % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60), mm = m % 60;
  return `${h}h ${mm}m`;
}
function pct(v: number): string { return `${Math.round(v * 100)}%`; }

function HomologacaoPage() {
  const [days, setDays] = useState(14);
  const [modeOn, setModeOn] = useState(false);
  useEffect(() => { setModeOn(isHomologModeOn()); }, []);

  const fetchOverview = useServerFn(getHomologacaoOverview);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["homologacao", days],
    queryFn: () => fetchOverview({ data: { days } }),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <PageHeader
        title="Homologação do Business Builder"
        description="Validação com utilizadores reais — sessões, tempos, abandono e feedback."
      />

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
        >
          <option value={7}>Últimos 7 dias</option>
          <option value={14}>Últimos 14 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={60}>Últimos 60 dias</option>
        </select>
        <button
          onClick={() => refetch()}
          className="rounded-md border border-black/10 bg-white px-3 py-2 text-sm hover:bg-black/[0.03]"
        >
          Atualizar
        </button>
        <button
          onClick={() => { const next = !modeOn; setHomologMode(next); setModeOn(next); }}
          className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${
            modeOn ? "bg-[var(--navy)] text-white" : "border border-black/10 bg-white text-[var(--navy)]"
          }`}
        >
          {modeOn ? <Eye size={14} /> : <EyeOff size={14} />}
          Modo homologação (admin): {modeOn ? "ativo" : "desligado"}
        </button>
      </div>

      {isLoading || !data ? (
        <div className="h-64 animate-pulse rounded-2xl bg-black/5" />
      ) : (
        <Overview data={data} />
      )}
    </div>
  );
}

function Overview({ data }: { data: HomologacaoOverview }) {
  return (
    <>
      <div className="grid gap-3 md:grid-cols-4">
        <Kpi label="Sessões iniciadas" value={data.sessionsStarted} />
        <Kpi label="Sessões concluídas" value={data.sessionsCompleted} />
        <Kpi label="Sessões abandonadas" value={data.sessionsAbandoned} />
        <Kpi label="Taxa de conclusão" value={pct(data.completionRate)} />
        <Kpi label="Tempo médio por seção" value={fmtDuration(data.avgSectionMs)} />
        <Kpi label="Tempo médio até plano concluído" value={fmtDuration(data.avgPlanMs)} />
      </div>

      <div className="rounded-2xl border border-black/5 bg-white p-5">
        <h2 className="font-display text-lg font-extrabold text-[var(--navy)]">Ranking de seções — dificuldade</h2>
        <p className="mt-1 text-xs text-[var(--gray-ink)]">Ordenado por taxa de abandono. Revisitas indicam correções.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wide text-[var(--gray-ink)]">
              <tr>
                <th className="py-2 pr-4">Seção</th>
                <th className="py-2 pr-4">Iniciadas</th>
                <th className="py-2 pr-4">Concluídas</th>
                <th className="py-2 pr-4">Abandonos</th>
                <th className="py-2 pr-4">Revisitas</th>
                <th className="py-2 pr-4">Tempo médio</th>
                <th className="py-2 pr-4">Abandono</th>
              </tr>
            </thead>
            <tbody>
              {data.sectionMetrics.length === 0 && (
                <tr><td colSpan={7} className="py-4 text-center text-[var(--gray-ink)]">Sem dados ainda.</td></tr>
              )}
              {data.sectionMetrics.map((s) => (
                <tr key={s.sectionKey} className="border-t border-black/5">
                  <td className="py-2 pr-4 font-semibold text-[var(--navy)]">{editorialLabel(s.sectionKey)}<br /><span className="text-[10px] font-mono text-[var(--gray-ink)]">{s.sectionKey}</span></td>
                  <td className="py-2 pr-4">{s.started}</td>
                  <td className="py-2 pr-4">{s.completed}</td>
                  <td className="py-2 pr-4">{s.abandoned}</td>
                  <td className="py-2 pr-4">{s.revisits}</td>
                  <td className="py-2 pr-4">{fmtDuration(s.avgDurationMs)}</td>
                  <td className="py-2 pr-4">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ${
                      s.abandonRate > 0.5 ? "bg-red-100 text-red-700"
                        : s.abandonRate > 0.2 ? "bg-amber-100 text-amber-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}>
                      {pct(s.abandonRate)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-black/5 bg-white p-5">
          <h2 className="font-display text-lg font-extrabold text-[var(--navy)]">Eventos recentes</h2>
          <ul className="mt-3 max-h-96 space-y-2 overflow-y-auto text-xs">
            {data.recentEvents.map((e) => (
              <li key={e.id} className="flex items-start justify-between gap-3 border-b border-black/5 pb-2">
                <div>
                  <p className="font-semibold text-[var(--navy)]">{e.event_name}</p>
                  <p className="text-[10px] text-[var(--gray-ink)]">
                    {new Date(e.at).toLocaleString("pt-BR")}
                    {e.section_key && <> · <span className="font-mono">{e.section_key}</span></>}
                    {e.mode && <> · {e.mode}</>}
                    {e.duration_ms != null && <> · {fmtDuration(e.duration_ms)}</>}
                  </p>
                </div>
                <span className="rounded bg-black/5 px-1.5 py-0.5 font-mono text-[10px] text-[var(--gray-ink)]">
                  {e.session_id?.slice(0, 6) ?? "—"}
                </span>
              </li>
            ))}
            {data.recentEvents.length === 0 && (
              <li className="py-4 text-center text-[var(--gray-ink)]">Nenhum evento no período.</li>
            )}
          </ul>
        </div>

        <div className="rounded-2xl border border-black/5 bg-white p-5">
          <h2 className="font-display text-lg font-extrabold text-[var(--navy)]">Feedback qualitativo</h2>
          <ul className="mt-3 max-h-96 space-y-3 overflow-y-auto text-xs">
            {data.feedback.map((f) => (
              <li key={f.id} className="rounded-lg border border-black/5 bg-black/[0.02] p-3">
                <p className="text-[10px] text-[var(--gray-ink)]">
                  {new Date(f.at).toLocaleString("pt-BR")} · {f.context ?? "manual"}
                </p>
                <p className="mt-1 whitespace-pre-line text-[var(--navy)]">{f.comment || "(sem comentário)"}</p>
              </li>
            ))}
            {data.feedback.length === 0 && (
              <li className="py-4 text-center text-[var(--gray-ink)]">Nenhum feedback ainda.</li>
            )}
          </ul>
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-black/[0.06] bg-white p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">{label}</p>
      <p className="mt-1 font-display text-2xl font-extrabold text-[var(--navy)]">{value}</p>
    </div>
  );
}
